<template>
  <div class="createWorkPermit">
    <TMSHeader
      :title="title"
      :buttons="buttons"
      :needGoback="true"
      extendStickyClass="createWorkPermit_stickyHeader"
      :dotOption="dotOption"
      :helpFunc="downloadUserGuide"
    >
      <div
        slot="status"
        class="pointer work-permit-status"
        :class="statusStyle"
        @click="lifecycleModal"
      >
        <span>{{ currentWorkPermit.status|customStatusLabel }}</span>
      </div>
      <i
        v-if="workerInfoLastUpdatedText"
        slot="status-detail"
        class="work-permit-last-updated"
      >
        {{ workerInfoLastUpdatedText }}
      </i>
    </TMSHeader>
    <Lifecycle
      ref="workPermitLifecycle"
      :modalId="`work-permit-modules-lifecycle-modal`"
      :commentAttr="lifecycleCommentAttrs.comments"
      :histories="histories || []"
      :type="'workPermit'"
    />
    <div class="createWorkPermit_allInfo">
      <Collapse
        id="generalInfo"
        :title="$t('workPermit.generalInfo')"
        :expand="expandMap.generalInfo"
        :keepContentAlive="true"
        class="standalone-detail__collapse"
        @expand-status="updateExpandMap"
      >
        <div
          slot="content"
          class="collapse-padding"
        >
          <GeneralInfo
            :key="currentWorkPermit.updatedAt"
            ref="step1_generalInfo"
            :isEdit="true"
            :isStandalone="true"
            :isProjectActive="true"
          />
        </div>
      </Collapse>

      <Collapse
        id="workerInfo"
        :title="$t('workPermit.workerInfo')"
        :expand="expandMap.workerInfo"
        :keepContentAlive="true"
        class="standalone-detail__collapse"
        @expand-status="updateExpandMap"
      >
        <div
          slot="content"
          class="collapse-padding"
        >
          <WorkerInfo
            ref="step2_workerInfo"
            :isEdit="true"
            :isStandalone="true"
          />
        </div>
      </Collapse>

      <Collapse
        v-if="showHoistingModule"
        id="hoisting"
        :title="hoistingLabel"
        :expand="expandMap.hoisting"
        :keepContentAlive="true"
        class="standalone-detail__collapse"
        @expand-status="updateExpandMap"
      >
        <div
          slot="content"
          class="collapse-padding"
        >
          <HoistingHotWork
            ref="step3_hoistingsInfo"
            :isEdit="true"
            :isStandalone="true"
            :isProjectActive="true"
          />
        </div>
      </Collapse>
    </div>
    <ActionConfirmModal
      ref="deleteWorkPermitModal"
      :modalId="`deleteWorkPermitModal_${workPermitId}`"
      :confirmTitle="isRejected ? deleteRejectedWPHint : $t('workPermit.deleteWPConfirmHint')"
    />
    <Modal
      ref="tooManyHoistingModal"
      :showCloseBtn="false"
    >
      <p class="tooManyHoistingModalMessage">
        {{ workTooManyHoistingMessage }}
      </p>
      <div slot="btns">
        <button
          type="button"
          class="btn btn--orange"
          @click="closeTooManyHoistingModal"
        >
          {{ $t('action.close') }}
        </button>
      </div>
    </Modal>
  </div>
</template>

<script>
import lodash from 'lodash'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import { computed, getCurrentInstance } from 'vue-demi'
import ActionConfirmModal from '@/controls/ActionConfirmModal'
import Collapse from '@/controls/Collapse'
import Lifecycle from '@/modules/Lifecycle'
import TMSHeader from '@/controls/TMSHeader'
import Modal from '@/controls/Modal'
import mutationTypes from '@/store/mutation-types'
import * as companiesApi from '@/api/companies/request'
import * as workPermitUtils from '@/api/workPermit/util'
import { companyType } from '@/utils/locale'
import { downloadFile } from '@/utils/xfs'
import { getDraftHoistingCountMap } from '@/utils/workPermitUtils'
import { getMaxWAPPRHoistingNum, isHoistingExceedMaxNum } from '@/api/workPermit/request'
import { getAttachments } from '@/api/docs/request'
import { sendEmail } from '@/api/postman/request'
import { utils as formUtils } from '@/plugin/InputChange'
import { DOCS_SOURCE_TYPE, EmailTypes, lifecycleCommentAttrs } from '@/utils/consts'
import notification from '@/utils/notification'
import { WORK_PERMIT_STATUS, WORK_PERMIT_TYPES, CreateWorkPermitTabs, DEFAULT_MAX_WAPPR_HOISTING_NUM } from './consts'
import { formatWorkPermitHistoriesForViewer, getHoistingExceedData, getWorkPermitHistoryRowStatus, getWorkerInfoLastUpdatedText, showHoistingByModuleNHoisting } from './utils'
import WorkerInfo from './WorkerInfo'
import HoistingHotWork from './HoistingHotWork'
import GeneralInfo from './GeneralInfo'
import {
  getDeleteRejectedWPHint,
  getRegionalWorkPermitLabel,
  getTooManyHoistingMessage,
} from './regionalWorkPermitLabels'

export default {
  name: 'StandaloneWpEdit',
  components: {
    ActionConfirmModal,
    Collapse,
    GeneralInfo,
    HoistingHotWork,
    Lifecycle,
    Modal,
    TMSHeader,
    WorkerInfo,
  },
  setup () {
    const { proxy } = getCurrentInstance()
    const showHoistingModule = computed(() => {
      return showHoistingByModuleNHoisting(proxy.currentWorkPermit.hoistings)
    })

    return {
      showHoistingModule,
    }
  },
  data () {
    return {
      expandMap: {
        generalInfo: true,
        workerInfo: true,
        hoisting: true,
      },
      lifecycleCommentAttrs,
      routeFrom: '',
      workPermitId: '',
      maxWAPPRHoistingNum: DEFAULT_MAX_WAPPR_HOISTING_NUM,
    }
  },
  computed: {
    ...mapGetters('workPermit', ['currentWorkPermit', 'parentPermit', 'workPermitsInProject']),
    ...mapGetters('user', ['isContractor', 'isTenant']),
    buttons () {
      return [{
        title: this.$t('action.save'),
        cls: 'btn--darkblue',
        func: this.save,
      }, {
        title: this.$t('action.submit'),
        cls: this.submitDisabled ? 'btn--grey unclickable' : 'btn--orange',
        func: this.submit,
      }]
    },
    title () {
      let code = this.currentWorkPermit.code
      code = code ? `${code} ` : ''
      return `${code}${this.$t('standaloneWorkPermit.label')}`
    },
    submitDisabled () {
      const parentId = (this.currentWorkPermit.parentId || '').trim()
      return parentId && (lodash.isEmpty(this.parentPermit) || this.isParentWPSuspended)
    },
    isParentWPSuspended () {
      return lodash.get(this, 'parentPermit.status', '') === WORK_PERMIT_STATUS.SUSPENDED
    },
    statusStyle () {
      return this.currentWorkPermit.status ? `workpermit-status-${this.currentWorkPermit.status.toLowerCase()} workpermit-status float-left` : ''
    },
    workerInfoLastUpdatedText () {
      return getWorkerInfoLastUpdatedText(this.currentWorkPermit, this.$t.bind(this))
    },
    isRejected () {
      const histories = lodash.get(this.currentWorkPermit, 'histories', [])
      return histories.some(
        h => getWorkPermitHistoryRowStatus(h) === WORK_PERMIT_STATUS.REJECTED,
      )
    },
    histories () {
      return formatWorkPermitHistoriesForViewer(this.currentWorkPermit.histories, {
        formatComments: false,
      })
    },
    dotOption () {
      return [{
        title: this.$t('action.delete'),
        operate: this.openConfirmDeleteModal,
      }]
    },
    hoistingLabel () {
      return getRegionalWorkPermitLabel('hoisting')
    },
    deleteRejectedWPHint () {
      return getDeleteRejectedWPHint()
    },
    workTooManyHoistingMessage () {
      return getTooManyHoistingMessage(
        this.maxWAPPRHoistingNum,
        this.$t('workPermit.HoistingLimitHintType.work'),
      )
    },
    detailRouteName () {
      return workPermitUtils.getStandaloneDetailRouteName(false, this.currentWorkPermit.isTenantProject)
    },
  },
  async mounted () {
    this.workPermitId = this.$route.params.id
    this.routeFrom = this.$route.query.from
    this.initHoistingPosition()
  },
  methods: {
    ...mapActions('workPermit', ['getWorkPermitById', 'updateWorkPermitById', 'getProjectPermit',
      'submitWorkPermit', 'deleteWorkPermit']),
    ...mapActions('tenantProject', ['loadTenantProject']),
    ...mapActions('projects', ['loadProject']),
    ...mapMutations('workPermit', {
      setWorkPermitStatus: mutationTypes.WORKPERMIT_SET_CURRENT_WORKPERMIT,
      updateFieldByName: mutationTypes.WORKPERMIT_SET_WORKPERMIT_FIELD_BY_NAME,
    }),
    async downloadUserGuide () {
      let guideFileId
      const attachments = await getAttachments(DOCS_SOURCE_TYPE.USER_GUIDE, true)
      if (this.isContractor) {
        guideFileId = this.getGuideFileIdByRole(attachments, this.$t('workPermit.hklVendor'), 'hklVendor')
      } else if (this.isTenant) {
        const isEntitledTenancyAccount = this.$refs.step1_generalInfo.isEntitledTenancyAccount
        if (isEntitledTenancyAccount) {
          guideFileId = this.getGuideFileIdByRole(attachments, this.$t('workPermit.tenant'), 'tenant')
        } else {
          guideFileId = this.getGuideFileIdByRole(attachments, this.$t('workPermit.tenantContractor'), 'contractor')
        }
      }
      if (guideFileId) {
        await downloadFile(`/docs/${guideFileId}`)
      } else {
        notification.fireNotification(false, this.$t('error.404'))
      }
    },
    lifecycleModal () {
      this.$refs.workPermitLifecycle.init()
    },
    navToDeatilPage () {
      this.$router.push({
        name: this.detailRouteName,
        param: { id: this.workPermitId },
      })
    },
    async submit () {
      if (!this.submitDisabled) {
        let workPermit = lodash.cloneDeep(this.currentWorkPermit)
        if (!workPermit.contractorId && lodash.get(workPermit, 'contractor.name', '')) {
          await this.addNewContractor(workPermit)
        }
        let validationHintMap = {
          'step1_generalInfo': this.$t('workPermit.generalInfo'),
          'step2_workerInfo': this.$t('workPermit.workerInfo'),
        }
        if (this.showHoistingModule) {
          validationHintMap = Object.assign(validationHintMap, {'step3_hoistingsInfo': this.hoistingLabel},
          )
        }
        const inValidHints = Object.entries(validationHintMap).map(([refName, hintTxt]) => {
          return !this.$refs[refName].validate() && hintTxt
        }).filter(v => Boolean(v))
        if (inValidHints.length) {
          notification.fireNotification(false, this.$t(`workPermit.emptyFieldMessage.${inValidHints.length}_empty`, inValidHints))
          return
        }

        this.maxWAPPRHoistingNum = await getMaxWAPPRHoistingNum()
        const draftHoistingCountMap = getDraftHoistingCountMap(this.currentWorkPermit)
        const contractorType = this.currentWorkPermit.workType
        const buildingId = workPermitUtils.getAttrOfBuilding(this.currentWorkPermit, 'id')
        this.updateFieldByName({name: 'contractorType', value: contractorType})
        const hoistingInfo = getHoistingExceedData(this.currentWorkPermit, draftHoistingCountMap, contractorType, buildingId)
        const isHoistingCountValid = await isHoistingExceedMaxNum(hoistingInfo)
        if (!isHoistingCountValid) {
          this.openTooManyHoistingModal()
          return
        }
        const newWP = await this.submitWorkPermit()
        let emailType
        if (newWP.status === WORK_PERMIT_STATUS.WAPPR) {
          emailType = newWP.type === WORK_PERMIT_TYPES.PROJECT ? EmailTypes.workPermits.firstApplyWP : EmailTypes.workPermits.applyWP
          await sendEmail(emailType, newWP)
          await sendEmail(EmailTypes.workPermits.applyWpToContractor, newWP)
        }
        formUtils.resetDirty()
        workPermitUtils.navigate(newWP, '')
        this.navToDeatilPage()
      }
    },
    async addNewContractor (workPermit) {
      const newContractor = await companiesApi.createCompany({ name: workPermit.contractor.name, type: companyType.PC })
      workPermit.contractor = newContractor
      workPermit.contractorId = newContractor.id
      if (lodash.get(workPermit, 'hoistings.length', -1) > 0) {
        workPermit.hoistings.forEach(hoisting => {
          hoisting.contractor = newContractor
          hoisting.contractorId = newContractor.id
        })
      }
      this.setWorkPermitStatus(workPermit)
    },
    async save () {
      const workPermit = lodash.cloneDeep(this.currentWorkPermit)
      if (!workPermit.contractorId && lodash.get(workPermit, 'contractor.name', '')) {
        await this.addNewContractor(workPermit)
      }
      await this.updateWorkPermitById()
      formUtils.resetDirty()
      this.navToDeatilPage()
    },
    async _deleteWorkPermit () {
      await this.deleteWorkPermit(this.currentWorkPermit)
      formUtils.resetDirty()
      workPermitUtils.deletePPNavigate(this.currentWorkPermit)
    },
    openConfirmDeleteModal () {
      this.$refs.deleteWorkPermitModal.open(this._deleteWorkPermit)
    },
    initHoistingPosition () {
      const hoistingId = this.$route.query.hoistingId
      if (hoistingId && +this.$route.query.tabIndex === CreateWorkPermitTabs.hoistingHotWork) {
        this.$refs.step3_hoistingsInfo.scrollTo(hoistingId)
      }
    },
    openTooManyHoistingModal () {
      this.$refs.tooManyHoistingModal.open()
    },
    closeTooManyHoistingModal () {
      this.$refs.tooManyHoistingModal.close()
    },
    updateExpandMap (id, status) {
      this.$set(this.expandMap, id, status)
    },
    handleWpExpand () {
      const wpExpandStatus = this.expandMap[this.$route.query.wpId]
      if (!wpExpandStatus) {
        this.$set(this.expandMap, this.$route.query.wpId, !this.$route.query.hoistingId)
      }
    },
    getGuideFileIdByRole (attachmentsArray, suffix, metaDataRoleName) {
      const commonFileName = this.$t('workPermit.saGuideFileName')
      const fileName = commonFileName + suffix + '.pdf'
      const guideFileIdArray = attachmentsArray.filter(attachment =>
        attachment.filename === fileName && attachment.metaData[metaDataRoleName] !== '0')
      return guideFileIdArray[0] && guideFileIdArray[0].id
    },
  },
}
</script>

<style lang='scss' scoped>
.createWorkPermit {
  background: #FAFBFC;
  .createWorkPermit_allInfo {
    max-width: 1440px;
    margin: 15px auto;
    background: white;
    padding: 20px 20px 0 20px;
    box-shadow: 0 2px 4px 0 rgba(212, 212, 212, 0.5);
    ::v-deep .standalone-detail__collapse {
      &>.title-container {
        border: none;
        border-bottom: 1px solid #b5b5b5;
        .title-content .default-title {
          font-family: Georgia;
          font-size: 20px;
          font-weight: 300;
        }
      }
      &>.content.with-border {
        border: none;
        .collapse-padding {
          padding: 25px 15px 15px 15px;
        }
      }
    }
  }
  .tms-header {
    padding-left: 15px;
    padding-right: 15px;
    .tms-header__box {
      position: relative;
      .title {
        font-size: 22px !important;
      }
      .button-grops {
        position: absolute;
        right: 0px;
        top: 30px;
      }
    }
    .createWorkPermit_stickyHeader {
      .button-grops {
        top: 0;
      }
      & > div {
        z-index: 101;
      }
      & ::v-deep .sticky-content {
        height: 0;
        overflow: visible;
      }
    }
  }
  .work-permit-last-updated {
    color: #878787;
    font-size: 12px;
    line-height: 20px;
    padding: 0 10px;
    white-space: nowrap;
  }
  .bold {
    font-weight: bold;
  }
}
</style>

<style scoped>
  .tooManyHoistingModalMessage {
    font-weight: bold;
  }
</style>
